// CartContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

// ---- helpers ----
const toPriceCents = (item) => {
  if (item?.priceCents != null) return Number(item.priceCents) || 0;
  if (typeof item?.price === 'string') {
    const n = parseFloat(item.price.replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }
  return 0;
};

const getMaxStockFor = (item) => {
  // supports: isOneOfOne OR inventory: { trackType: 'stock', stockQty: N }
  if (item?.isOneOfOne) return 1;
  const trackType = item?.inventory?.trackType;
  const stockQty = Number(item?.inventory?.stockQty ?? NaN);
  if (trackType === 'stock' && Number.isFinite(stockQty)) {
    return Math.max(0, stockQty);
  }
  return Infinity; // made-to-order/unlimited
};

const STORAGE_KEY = 'neneCart';
const FULFILLMENT_KEY = 'neneCart_fulfillment';
export const FLAT_SHIPPING_CENTS = 1000; // $10 flat rate when shipping

export const CartProvider = ({ children }) => {
  // ---- Cart state (with legacy key migration) ----
  const [cart, setCart] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);

      // migrate legacy key once, if present
      const legacy = localStorage.getItem('nenenail_cart');
      if (legacy) {
        const parsed = JSON.parse(legacy);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
        localStorage.removeItem('nenenail_cart');
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch {
      return [];
    }
  });

  // ---- Cart-level fulfillment (shipping | in_person) ----
  const [orderFulfillment, setOrderFulfillment] = useState(() => {
    try {
      const stored = localStorage.getItem(FULFILLMENT_KEY);
      return stored === 'in_person' ? 'in_person' : 'shipping';
    } catch {
      return 'shipping';
    }
  });

  // Persist cart & fulfillment
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(FULFILLMENT_KEY, orderFulfillment);
  }, [orderFulfillment]);

  // ---- helpers exposed to UI ----
  const getQtyInCart = (idOrSlugOrSku) => {
    const key = String(idOrSlugOrSku || '');
    const line = cart.find(
      (i) =>
        i.id === key ||
        i.slug === key ||
        (i.sku && i.sku === key) ||
        (i.variantKey && i.variantKey === key)
    );
    return Number(line?.quantity || 0);
  };

  // ---- Cart ops ----
  /** Add or merge an item (returns boolean: added or not)
   * Merge priority:
   * 1) variantKey (best)
   * 2) one-of-one by sku or slug
   * 3) legacy fields (slug+color+shape+length+notes)
   */
  const addToCart = (newItem) => {
    let didAdd = false;

    setCart((prev) => {
      const next = [...prev];
      const qtyToAdd = Math.max(1, Number(newItem?.quantity || 1));
      const oneOfOne = !!newItem?.isOneOfOne;

      // Try matches in priority order
      let matchIndex = -1;

      // 1) variantKey
      if (newItem?.variantKey) {
        matchIndex = next.findIndex((i) => i.variantKey === newItem.variantKey);
      }

      // 2) one-of-one by sku or slug
      if (matchIndex === -1 && oneOfOne) {
        matchIndex = next.findIndex(
          (i) =>
            (i.sku && newItem.sku && i.sku === newItem.sku) ||
            i.slug === newItem.slug
        );
      }

      // 3) legacy composite (only if still no match and not one-of-one)
      if (matchIndex === -1 && !newItem?.variantKey && !oneOfOne) {
        matchIndex = next.findIndex(
          (i) =>
            i.slug === newItem.slug &&
            (i.color?.name || '') === (newItem.color?.name || '') &&
            (i.shape || '') === (newItem.shape || '') &&
            (i.length || '') === (newItem.length || '') &&
            (i.notes || '') === (newItem.notes || '')
        );
      }

      const maxStock = getMaxStockFor(newItem);

      // ---- Merge into existing line
      if (matchIndex !== -1) {
        const existing = { ...next[matchIndex] };
        const currentQty = Number(existing.quantity || 1);
        const desiredQty = oneOfOne ? 1 : currentQty + qtyToAdd;
        const finalQty =
          maxStock === Infinity ? desiredQty : Math.min(desiredQty, maxStock);

        if (finalQty <= currentQty) {
          didAdd = false;
          return next; // already at cap, nothing added
        }

        existing.quantity = finalQty;

        // Prefer new fulfillment if provided (not required)
        if (newItem.fulfillment) {
          existing.fulfillment = newItem.fulfillment;
        }

        // Normalize priceCents (ensure it exists for totals)
        existing.priceCents =
          existing.priceCents != null
            ? Number(existing.priceCents) || 0
            : toPriceCents({ ...existing, ...newItem });

        next[matchIndex] = existing;
        didAdd = true;
        return next;
      }

      // ---- New line
      const line = {
        ...newItem,
        fulfillment: newItem.fulfillment || 'shipping',
      };

      const intended = oneOfOne ? 1 : qtyToAdd;
      line.quantity =
        maxStock === Infinity ? intended : Math.max(1, Math.min(intended, maxStock));

      // price cents normalization
      line.priceCents = toPriceCents(line);

      if (line.quantity < 1) {
        didAdd = false; // no stock available
        return next;
      }

      next.push(line);
      didAdd = true;
      return next;
    });

    return didAdd;
  };

  const updateQuantity = (index, newQty) => {
    setCart((prev) => {
      if (index < 0 || index >= prev.length) return prev;

      const item = prev[index];
      const maxStock = getMaxStockFor(item);

      // Respect one-of-one and stock limits
      const intended = item.isOneOfOne ? 1 : Math.max(1, Number(newQty) || 1);
      const clamped =
        maxStock === Infinity ? intended : Math.max(1, Math.min(intended, maxStock));

      const next = [...prev];
      next[index] = { ...item, quantity: clamped };

      // normalize priceCents if missing
      next[index].priceCents =
        next[index].priceCents != null
          ? Number(next[index].priceCents) || 0
          : toPriceCents(next[index]);

      return next;
    });
  };

  const removeFromCart = (index) =>
    setCart((prev) => prev.filter((_, i) => i !== index));

  const clearCart = () => setCart([]);

  // (Optional) Per-line fulfillment toggle if you ever surface it in UI
  const updateFulfillment = (index, fulfillment) => {
    setCart((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = { ...next[index], fulfillment };
      return next;
    });
  };

  // ---- Totals ----
  const subtotalCents = useMemo(
    () =>
      cart.reduce((sum, item) => {
        const unit =
          item.priceCents != null
            ? Number(item.priceCents) || 0
            : toPriceCents(item);
        const qty = Number(item.quantity || 1);
        return sum + unit * qty;
      }, 0),
    [cart]
  );

  // Cart-level fulfillment drives shipping (ignore per-item flags)
  const shippingCents = useMemo(() => {
    if (cart.length === 0) return 0;
    return orderFulfillment === 'in_person' ? 0 : FLAT_SHIPPING_CENTS;
  }, [cart, orderFulfillment]);

  const totalCents = subtotalCents + shippingCents;

  const formatUSD = (cents) =>
    (Number(cents || 0) / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });

  const value = {
    // state
    cart,

    // ops
    addToCart,            // returns boolean
    updateQuantity,
    updateFulfillment,    // optional; unused if you only do cart-level
    removeFromCart,
    clearCart,

    // helpers
    getQtyInCart,

    // totals
    subtotalCents,
    shippingCents,
    totalCents,
    formatUSD,

    // cart-level fulfillment
    orderFulfillment,
    setOrderFulfillment,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
